import { Directive, effect, inject, Input, TemplateRef, ViewContainerRef } from '@angular/core';
import { PermissionService } from '../../core/services/permission.service';

/**
 * Structural directive that shows/hides elements based on user permissions.
 *
 * @example
 * <!-- Single permission -->
 * <button *hasPermission="'asset.create'">Create Asset</button>
 *
 * <!-- Multiple permissions (OR logic - default) -->
 * <button *hasPermission="['asset.create', 'asset.update']">
 *   Add/Edit Asset
 * </button>
 *
 * <!-- Multiple permissions (AND logic) -->
 * <div *hasPermission="['org.settings', 'org.billing']; mode: 'all'">
 *   Billing Settings
 * </div>
 *
 * <!-- With feature flags -->
 * <section *hasPermission="'feat.photos'">
 *   Photo upload feature
 * </section>
 */
@Directive({
  selector: '[hasPermission]',
  standalone: true
})
export class HasPermissionDirective {
  private permissionService = inject(PermissionService);
  private templateRef = inject(TemplateRef);
  private viewContainer = inject(ViewContainerRef);

  /**
   * Single permission string or array of permission strings to check
   */
  @Input() hasPermission!: string | string[];

  /**
   * Mode for checking multiple permissions:
   * - 'any': User needs at least one permission (OR logic) - default
   * - 'all': User needs all permissions (AND logic)
   */
  @Input() hasPermissionMode: 'any' | 'all' = 'any';

  constructor() {
    // Create effect to watch permission changes and update view reactively
    effect(() => {
      const hasAccess = this.checkPermissions();

      // Update view based on permission check
      if (hasAccess) {
        // Show element by creating embedded view
        // Only create if not already created
        if (this.viewContainer.length === 0) {
          this.viewContainer.createEmbeddedView(this.templateRef);
        }
      } else {
        // Hide element by clearing view
        this.viewContainer.clear();
      }
    });
  }

  /**
   * Check if user has required permissions based on current configuration
   */
  private checkPermissions(): boolean {
    // Handle empty or invalid input
    if (!this.hasPermission) {
      return false;
    }

    // Normalize to array for consistent handling
    const permissions = Array.isArray(this.hasPermission)
      ? this.hasPermission
      : [this.hasPermission];

    // Handle empty array
    if (permissions.length === 0) {
      return false;
    }

    // Filter out empty strings
    const validPermissions = permissions.filter(p => p && p.trim().length > 0);
    if (validPermissions.length === 0) {
      return false;
    }

    // Check permissions based on mode
    if (this.hasPermissionMode === 'all') {
      return this.permissionService.hasAll(validPermissions);
    } else {
      return this.permissionService.hasAny(validPermissions);
    }
  }
}
